import { useState, useEffect } from 'react';
import { getLogger } from '@confluence/logger';

const logger = getLogger('highlight-action');

const TRELLO_HAS_ACCESS_ENDPOINT = '/wiki/api/v2/trello/inbox/has-access';

export interface TrelloAccessState {
	hasTrelloAccess: boolean;
	loading: boolean;
	error: boolean;
}

let cachedResult: TrelloAccessState | null = null;
let fetchPromise: Promise<TrelloAccessState> | null = null;

export function resetTrelloAccessCache(): void {
	cachedResult = null;
	fetchPromise = null;
}

function cacheErrorResult(): TrelloAccessState {
	const result: TrelloAccessState = { hasTrelloAccess: false, loading: false, error: true };
	cachedResult = result;
	return result;
}

export function prefetchTrelloAccess(): Promise<TrelloAccessState> {
	if (!fetchPromise) {
		fetchPromise = Promise.resolve()
			.then(() =>
				fetch(TRELLO_HAS_ACCESS_ENDPOINT, {
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
					},
				}),
			)
			.then(async (response) => {
				if (!response.ok) {
					logger.warn`Failed to check Trello access: status=${response.status}`;
					return cacheErrorResult();
				}

				const data = (await response.json()) as { hasTrelloAccess?: boolean };
				const result: TrelloAccessState = {
					hasTrelloAccess: Boolean(data.hasTrelloAccess),
					loading: false,
					error: false,
				};
				cachedResult = result;
				return result;
			})
			.catch((error) => {
				logger.error`Error checking Trello access: ${error}`;
				return cacheErrorResult();
			});
	}
	return fetchPromise;
}

/**
 * Hook to check if the current user has Trello access.
 * Uses the Identity Platform permissions service to verify the user has
 * both 'use/site' and 'write' permissions on Trello.
 *
 * Results are cached at module level so the fetch only happens once per
 * app lifecycle. Subsequent calls return the cached result instantly.
 *
 * @returns TrelloAccessState - { hasTrelloAccess, loading, error }
 */
export const useTrelloAccess = (): TrelloAccessState => {
	const [state, setState] = useState<TrelloAccessState>(
		() => cachedResult ?? { hasTrelloAccess: false, loading: true, error: false },
	);

	useEffect(() => {
		if (cachedResult) {
			setState(cachedResult);
			return;
		}

		let cancelled = false;
		void prefetchTrelloAccess().then((result) => {
			if (!cancelled) {
				setState(result);
			}
		});

		return () => {
			cancelled = true;
		};
	}, []);

	return state;
};
